import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from 'react-router-dom';

interface PremiumDialogContextType {
  isOpen: boolean;
  isProcessing: boolean;
  isVerifying: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  handleSubscribe: () => Promise<void>;
  handleManageSubscription: () => Promise<void>;
  verifySubscriptionStatus: (options?: { force?: boolean, email?: string, checkoutSessionId?: string }) => Promise<boolean>;
  subscriptionRenewalDate: string | null;
  subscriptionStatus: string | null;
}

const PremiumDialogContext = createContext<PremiumDialogContextType | undefined>(undefined);

export const PremiumDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [subscriptionRenewalDate, setSubscriptionRenewalDate] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const { toast } = useToast();
  const location = useLocation();
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    return () => {
      setIsProcessing(false);
      setIsOpen(false);
      if (lastFocusedElementRef.current) {
        lastFocusedElementRef.current.removeAttribute('data-last-focused');
      }
    };
  }, []);

  const openDialog = useCallback(() => {
    lastFocusedElementRef.current = document.activeElement as HTMLElement;
    if (lastFocusedElementRef.current) {
      lastFocusedElementRef.current.setAttribute('data-last-focused', 'true');
    }
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    if (!isProcessing) {
      setIsOpen(false);
    }
  }, [isProcessing]);

  const verifySubscriptionStatus = async (options?: { force?: boolean, email?: string, checkoutSessionId?: string }): Promise<boolean> => {
    if (isVerifying) return false;
    
    try {
      setIsVerifying(true);
      console.log('Verifying subscription status...', { options });
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session && !options?.email) {
        console.log('No active session found for subscription verification');
        return false;
      }

      const { data, error } = await supabase.functions.invoke('verify-subscription', {
        body: { 
          userId: session?.user.id,
          forceVerify: options?.force,
          email: options?.email,
          checkoutSessionId: options?.checkoutSessionId
        },
        headers: {
          Authorization: session ? `Bearer ${session.access_token}` : '',
        },
      });

      if (error) {
        console.error('Error verifying subscription:', error);
        return false;
      }

      let found = false;
      if (data) {
        const details = Array.isArray(data.details) ? data.details : [];
        const subObj = data.subscription_result || details[0] || data;
        if (subObj) {
          if (subObj.subscription_end) {
            setSubscriptionRenewalDate(subObj.subscription_end);
            found = true;
          } else {
            setSubscriptionRenewalDate(null);
          }
          if (subObj.status) {
            setSubscriptionStatus(subObj.status);
          } else if (typeof subObj.premium === 'boolean') {
            setSubscriptionStatus(subObj.premium ? "active" : "inactive");
          } else {
            setSubscriptionStatus(null);
          }
        }
      }

      if (data && data.details && data.details.length > 0) {
        for (const userResult of data.details) {
          if (userResult.action === 'upgraded' || userResult.premium === true) {
            toast({
              title: "Premium access activated!",
              description: "Your account has been upgraded to premium. Enjoy the new features!",
            });
            return true;
          }
        }
      }
      if (data && data.checkout_verified && data.subscription_result?.premium) {
        toast({
          title: "Premium access activated!",
          description: "Your account has been upgraded to premium. Enjoy the new features!",
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in verifySubscriptionStatus:', error);
      setSubscriptionRenewalDate(null);
      setSubscriptionStatus(null);
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubscribe = async () => {
    if (isProcessing) return;
    
    try {
      console.log('[Premium Dialog][Context] Starting subscription...', {
        location: location.pathname,
        timestamp: new Date().toISOString()
      });
      
      setIsProcessing(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to subscribe",
          variant: "destructive",
        });
        return;
      }

      const { data: checkoutSession, error: checkoutError } = await supabase
        .from('checkout_sessions')
        .insert({
          user_id: session.user.id,
          pre_checkout_route: location.pathname,
          session_id: '',
          status: 'pending'
        })
        .select()
        .single();

      if (checkoutError) {
        console.error('[Premium Dialog][Context] Database error:', {
          error: checkoutError,
          timestamp: new Date().toISOString()
        });
        throw new Error('Could not initialize checkout session');
      }

      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          priceId: 'price_1QolOdHIuEGZaLJEmyjPeVaa',
          checkoutSessionId: checkoutSession.id
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (!data?.url || !data?.sessionId) {
        throw new Error('Invalid checkout session response');
      }

      const { error: updateError } = await supabase
        .from('checkout_sessions')
        .update({ session_id: data.sessionId })
        .eq('id', checkoutSession.id);

      if (updateError) {
        console.error('[Premium Dialog][Context] Failed to update session:', {
          error: updateError,
          timestamp: new Date().toISOString()
        });
      }

      sessionStorage.setItem('lastCheckoutSessionId', checkoutSession.id);

      window.open(data.url, '_blank');
      setIsOpen(false);
    } catch (error) {
      console.error('[Premium Dialog][Context] Error:', {
        error,
        timestamp: new Date().toISOString()
      });
      toast({
        title: "Error",
        description: "Could not initiate checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    if (isProcessing) return;
    
    try {
      console.log('[Premium Dialog][Context] Opening subscription management portal...');
      
      setIsProcessing(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to manage your subscription",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (!data?.url) {
        throw new Error('Invalid portal session response');
      }

      window.open(data.url, '_blank');
    } catch (error) {
      console.error('[Premium Dialog][Context] Error opening portal:', {
        error,
        timestamp: new Date().toISOString()
      });
      toast({
        title: "Error",
        description: "Could not open subscription management portal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <PremiumDialogContext.Provider 
      value={{ 
        isOpen, 
        isProcessing,
        isVerifying, 
        openDialog, 
        closeDialog, 
        handleSubscribe,
        handleManageSubscription,
        verifySubscriptionStatus,
        subscriptionRenewalDate,
        subscriptionStatus
      }}
    >
      {children}
    </PremiumDialogContext.Provider>
  );
};

export const usePremiumDialog = () => {
  const context = useContext(PremiumDialogContext);
  if (context === undefined) {
    throw new Error('usePremiumDialog must be used within a PremiumDialogProvider');
  }
  return context;
};
